// Wire protocol shared by the worker and the browser clients.
// JSON text frames everywhere, except desk audio (binary PCM16LE, 16 kHz mono).

export const PROTOCOL_VERSION = 1;

export type Lang = string; // BCP-47: "en", plus the translated languages in config/settings.json
export type Mode = "text" | "audio"; // "audio" reserved for phase 4
export type RoomState = "idle" | "live" | "paused" | "reconnecting";
export type UpstreamState = "connecting" | "open" | "error" | "closed";

export const SUPPORTED_MODES: Mode[] = ["text"];

/** A state counts as "active" between Start and Stop; listener routes only open then. */
export function isActiveState(s: RoomState): boolean {
  return s !== "idle";
}

/** WebSocket close codes used by the room. */
export const CloseCode = {
  /** Session stopped: go back to waiting, do not reconnect. */
  Ended: 4001,
  /** The same desk (same tab id) connected again; this socket is stale. */
  Replaced: 4002,
  /** Bad message / protocol violation. */
  Protocol: 4003,
  /** This desk device's approval was removed: show why, don't reconnect. */
  Revoked: 4004,
} as const;

export interface CaptionLine {
  id: number;
  text: string;
  ts: number;
}

// ---- Listener -> DO
export interface SubscribeMsg {
  type: "subscribe";
  lang: Lang;
  mode: Mode;
  /** Last final id the client already has (for reconnect without duplicates). */
  lastId?: number;
  /** Epoch the client's lines belong to. A mismatch means the client must clear its lines. */
  epoch?: string;
}
export interface PingMsg {
  type: "ping";
}
export type ListenerToRoom = SubscribeMsg | PingMsg;

// ---- DO -> listener (desk receives these too, for its preview)
export interface HelloMsg {
  type: "hello";
  v: number;
  room: string;
  epoch: string;
  state: RoomState;
  langs: Lang[];
  modes: Mode[];
}
export interface BacklogMsg {
  type: "backlog";
  epoch: string;
  /** true when `lines` is the complete backlog and the client should replace what it has. */
  reset: boolean;
  lines: CaptionLine[];
}
export interface InterimMsg {
  type: "interim";
  text: string;
}
export interface FinalMsg extends CaptionLine {
  type: "final";
  /** Set on translated lines (absent = English). */
  lang?: Lang;
  /** A translated reader got the English line: the model failed or fell behind. */
  fallback?: boolean;
}
export interface StatusMsg {
  type: "status";
  state: RoomState;
}
export interface ErrorMsg {
  type: "error";
  code: string;
  message: string;
}
export interface PongMsg {
  type: "pong";
}
export type RoomToListener =
  | HelloMsg
  | BacklogMsg
  | InterimMsg
  | FinalMsg
  | StatusMsg
  | ErrorMsg
  | PongMsg;

// ---- Desk -> DO (plus binary audio frames)
export interface StartMsg {
  type: "start";
  /** Free text, e.g. "Romans 8; Psalm 23". Used to pick extra keyterms. */
  readings?: string;
  /** Extra keyterms for this service, e.g. a guest speaker's name. */
  terms?: string[];
}
export interface ResumeMsg {
  type: "resume";
  readings?: string;
  terms?: string[];
}
/**
 * Desks connect to /ws/<room>/desk?id=<desk id>; the id is per browser tab, so a refresh
 * keeps control. Only the active desk's audio is used. Any other desk is a monitor until it
 * sends "takeover", or presses Start while the room is idle; or it can act as a remote
 * control: controls sent with `remote: true` are obeyed, but the audio stays with the
 * desk in control.
 */
export type DeskToRoom =
  | StartMsg
  | ResumeMsg
  | { type: "pause" }
  | { type: "stop" }
  | { type: "takeover" }
  /** Wipe the lines on every screen without ending the session (e.g. after a pre-service test). */
  | { type: "clear" }
  | PingMsg;

// ---- DO -> desk
export interface DeskStatusMsg {
  type: "desk_status";
  /** This desk is the one in control (its audio and buttons are used). */
  active: boolean;
  /** Some desk is in control and connected (this one or another). */
  activeDeskConnected: boolean;
  /** This desk is open on a phone. */
  phone: boolean;
  /** The desk in control is a computer and connected: a phone can only be a remote control. */
  computerInControl: boolean;
  state: RoomState;
  upstream: UpstreamState;
  listeners: number;
  /** Readers per language, e.g. { en: 5, uk: 2 }. */
  listenersByLang: Record<Lang, number>;
  keyterms: string[];
  readings: string;
  terms: string[];
}
export interface DeskRejectedMsg {
  type: "desk_rejected";
  /** The control that was ignored. */
  action: string;
  /** "not_in_control" (default); "no_audio_desk": a remote Start with no desk computer connected;
   *  "computer_in_control": a phone tried to take over (or Start) while a computer runs the desk. */
  reason?: "not_in_control" | "no_audio_desk" | "computer_in_control";
}
export interface UpstreamErrorMsg {
  type: "upstream_error";
  message: string;
}
export type RoomToDesk = RoomToListener | DeskStatusMsg | DeskRejectedMsg | UpstreamErrorMsg;

export interface RoomStatusResponse {
  room: string;
  active: boolean;
  state: RoomState;
  /** Languages readers can choose (so the waiting screen can offer them). */
  langs?: Lang[];
}

/** Desk audio format. */
export const AUDIO_SAMPLE_RATE = 16000;
export const AUDIO_FRAME_MS = 100;
export const AUDIO_FRAME_BYTES = (AUDIO_SAMPLE_RATE * AUDIO_FRAME_MS) / 1000 * 2;
export const MAX_AUDIO_FRAME_BYTES = 64 * 1024;
